import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { DocHistory, docHistorySource, undoService } from './undo.js';

/**
 * Histories by source id, kept for the session so closing and reopening an
 * editor (or switching tabs) does not lose its undo steps.
 */
const histories = new Map<string, DocHistory<unknown>>();

export function docHistory<T>(id: string, limit?: number, coalesceMs?: number): DocHistory<T> {
  let history = histories.get(id) as DocHistory<T> | undefined;
  if (!history) {
    history = new DocHistory<T>(limit, coalesceMs);
    histories.set(id, history as DocHistory<unknown>);
  }
  return history;
}

export interface DocHistoryHandle<T> {
  /** Record a step that started from `before`. Same `key` within a moment merges (slider drags). */
  record(before: T, label: string, key?: string): void;
  undo(): void;
  redo(): void;
  canUndo: boolean;
  canRedo: boolean;
  /** Label of the step Undo would revert. */
  undoLabel: string | null;
  redoLabel: string | null;
}

/**
 * Snapshot undo for an editor, registered with the shared undo service while
 * mounted: Ctrl+Z / Ctrl+Shift+Z, the Edit menu and the Undo History panel
 * all reach it. `read` returns the current document, `apply` shows a state.
 */
export function useDocHistory<T>(options: {
  id: string;
  label: string;
  view: string;
  read: () => T;
  apply: (state: T) => void;
  limit?: number;
  coalesceMs?: number;
}): DocHistoryHandle<T> {
  const { id, label, view, limit, coalesceMs } = options;
  const history = docHistory<T>(id, limit, coalesceMs);
  const io = useRef({ read: options.read, apply: options.apply });
  io.current = { read: options.read, apply: options.apply };

  useEffect(
    () =>
      undoService.register(
        docHistorySource({
          id,
          label,
          view,
          history,
          read: () => io.current.read(),
          apply: (state) => io.current.apply(state),
        }),
      ),
    [id, label, view, history],
  );

  useSyncExternalStore(
    (fn) => history.subscribe(fn),
    () => `${history.snapshot().past.length}:${history.snapshot().future.length}`,
  );

  const record = useCallback(
    (before: T, stepLabel: string, key?: string) => history.record(before, stepLabel, key),
    [history],
  );
  const undo = useCallback(() => {
    undoService.touch(id);
    const state = history.undo(io.current.read());
    if (state !== undefined) io.current.apply(state);
  }, [history, id]);
  const redo = useCallback(() => {
    undoService.touch(id);
    const state = history.redo(io.current.read());
    if (state !== undefined) io.current.apply(state);
  }, [history, id]);

  const snap = history.snapshot();
  return {
    record,
    undo,
    redo,
    canUndo: snap.past.length > 0,
    canRedo: snap.future.length > 0,
    undoLabel: snap.past.at(-1)?.label ?? null,
    redoLabel: snap.future[0]?.label ?? null,
  };
}
